import { t } from '@lingui/core/macro';
import type { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';

import { loadCognito } from './cognito';
import { createCognitoFlows, normalizeEmail, totpUri, type AuthResult, type CognitoFlows, type PoolConfig } from './cognito-flows';

export type AuthStatus = 'loading' | 'signedOut' | 'challenge' | 'signedIn';
export type ChallengeFlow = 'mfa' | 'mfaSetup' | 'newPassword';

export interface AuthUser {
  /** Username do Cognito (o e-mail). */
  username: string;
  name?: string;
  email?: string;
  verified: boolean;
  /** `sub` do access token (o `userSubId` do legado). */
  sub?: string;
}

export interface AuthState {
  status: AuthStatus;
  challenge?: ChallengeFlow;
  user?: AuthUser;
  /** E-mail digitado no login, reaproveitado em nova senha (o `EmailContext` do legado). */
  email?: string;
  /** `otpauth://…` do QR no setup de MFA. */
  mfaSetupUri?: string;
  /** Mensagem do último erro (o `submitError` do legado), já traduzida. */
  error?: string;
  /** Código de e-mail (recuperação ou confirmação) enviado. */
  codeSent?: boolean;
  pending: boolean;
}

export interface AuthSessionOptions {
  pool: PoolConfig;
  mainTitle?: string | null | undefined;
  loadFlows?: () => Promise<CognitoFlows>;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  /** Onde a lib guarda os tokens (`localStorage`); o logout limpa direto se a lib falhar. */
  cognitoStorage?: Pick<Storage, 'length' | 'key' | 'removeItem'>;
  /** Intervalo do polling de sessão; o legado usava 3 s. */
  pollMs?: number;
  onSignedIn?: (user: AuthUser) => void;
  onSignedOut?: () => void;
}

const FLOW_KEY = 'uyzy.auth.flow';
const REDIRECT_KEY = 'uyzy.auth.redirect';

function safeSessionStorage(): AuthSessionOptions['storage'] {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
}

function safeLocalStorage(): AuthSessionOptions['cognitoStorage'] {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

type Listener = () => void;

/**
 * Sessão Cognito fiel ao `IdentityProvider` do legado: login, desafios, polling de sessão e tokens.
 * É um store externo (useSyncExternalStore), fora das 3 stores Zustand da U22.
 */
export class AuthSession {
  #state: AuthState = { status: 'loading', pending: false };
  readonly #listeners = new Set<Listener>();
  readonly #options: AuthSessionOptions;
  readonly #storage: AuthSessionOptions['storage'];
  #flows: Promise<CognitoFlows> | undefined;
  #challengeUser: CognitoUser | undefined;
  #ready: Promise<AuthState> | undefined;
  #poll: ReturnType<typeof setInterval> | undefined;
  #inflight: Promise<CognitoUserSession | null> | undefined;

  constructor(options: AuthSessionOptions) {
    this.#options = options;
    this.#storage = options.storage ?? safeSessionStorage();
  }

  getState = (): AuthState => this.#state;

  subscribe = (listener: Listener) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  // `undefined` no patch remove a chave (exactOptionalPropertyTypes).
  #set(patch: { [K in keyof AuthState]?: AuthState[K] | undefined }, replace = false) {
    const next: Record<string, unknown> = replace ? { pending: false } : { ...this.#state };
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined) Reflect.deleteProperty(next, key);
      else next[key] = value;
    }
    this.#state = next as unknown as AuthState;
    for (const listener of this.#listeners) listener();
  }

  #loadFlows(): Promise<CognitoFlows> {
    this.#flows ??= this.#options.loadFlows
      ? this.#options.loadFlows()
      : loadCognito().then((cognito) => createCognitoFlows(cognito, this.#options.pool));
    return this.#flows;
  }

  /** Resolve quando a restauração do boot termina; o `requireAuth` espera isto. */
  ready(): Promise<AuthState> {
    this.#ready ??= this.#restore();
    return this.#ready;
  }

  async #restore(): Promise<AuthState> {
    const flows = await this.#loadFlows();
    const user = flows.currentUser();
    if (!user) {
      this.#set({ status: 'signedOut' }, true);
      return this.#state;
    }
    const session = await flows.getSession(user);
    if (!session) {
      user.signOut();
      this.#set({ status: 'signedOut' }, true);
      return this.#state;
    }
    if (this.#options.pool.useMfa) {
      const hasMfa = await flows.hasMfaConfigured(user);
      if (hasMfa === null) {
        void this.signOut();
        return this.#state;
      }
      // Mais estrito que o legado: sem TOTP configurado nunca entra, mesmo sem o segredo salvo.
      if (!hasMfa) {
        const saved = this.#readFlow();
        const result: AuthResult =
          saved?.type === 'mfaSetup' && saved.secretCode
            ? { type: 'mfaSetup', user, secretCode: saved.secretCode }
            : await flows.associateSoftwareToken(user);
        await this.#apply(result);
        return this.#state;
      }
    }
    await this.#enterSignedIn(user, session);
    return this.#state;
  }

  #readFlow(): { type?: string; secretCode?: string } | undefined {
    try {
      const raw = this.#storage?.getItem(FLOW_KEY);
      return raw ? (JSON.parse(raw) as { type?: string; secretCode?: string }) : undefined;
    } catch {
      return undefined;
    }
  }

  async #enterSignedIn(user: CognitoUser, known?: CognitoUserSession) {
    const flows = await this.#loadFlows();
    const session = known ?? (await flows.getSession(user));
    if (!session) {
      void this.signOut();
      return;
    }
    const profile = await flows.getProfile(user);
    const sub = session.getAccessToken().payload['sub'] as string | undefined;
    const authUser: AuthUser = {
      username: user.getUsername(),
      verified: profile?.verified ?? false,
      ...(profile?.name ? { name: profile.name } : {}),
      ...(profile?.email ? { email: profile.email } : {}),
      ...(sub ? { sub } : {}),
    };
    this.#challengeUser = undefined;
    this.#storage?.removeItem(FLOW_KEY);
    const email = this.#state.email;
    this.#set({ status: 'signedIn', user: authUser, ...(email ? { email } : {}) }, true);
    this.#startPolling();
    this.#options.onSignedIn?.(authUser);
  }

  async #apply(result: AuthResult) {
    switch (result.type) {
      case 'success':
        await this.#enterSignedIn(result.user);
        return;
      case 'failure':
        // Recusa que chega depois de a sessão já ter entrado (tentativa concorrente): descartada.
        if (this.#state.status === 'signedIn') return;
        if (result.user && this.#state.status === 'challenge') this.#challengeUser = result.user;
        this.#set({ error: result.message, pending: false });
        return;
      case 'mfa':
      case 'newPassword':
        this.#challengeUser = result.user;
        this.#set({ status: 'challenge', challenge: result.type, pending: false, error: undefined });
        return;
      case 'mfaSetup': {
        this.#challengeUser = result.user;
        const secret = result.secretCode;
        if (secret) this.#storage?.setItem(FLOW_KEY, JSON.stringify({ type: 'mfaSetup', secretCode: secret }));
        const uri = secret ? totpUri(result.user.getUsername(), secret, this.#options.mainTitle) : undefined;
        this.#set({
          status: 'challenge',
          challenge: 'mfaSetup',
          pending: false,
          user: { username: result.user.getUsername(), verified: false },
          ...(uri ? { mfaSetupUri: uri } : {}),
        });
        return;
      }
      case 'verificationEmail':
        this.#set({ codeSent: true, pending: false, error: undefined });
        return;
    }
  }

  async #run(step: (flows: CognitoFlows) => Promise<AuthResult>) {
    this.#set({ pending: true, error: undefined });
    try {
      await this.#apply(await step(await this.#loadFlows()));
    } catch {
      if (this.#state.status !== 'signedIn') this.#set({ pending: false, error: t`Erro inesperado. Tente novamente.` });
    }
    return this.#state;
  }

  #requireChallenge(flow: ChallengeFlow): CognitoUser | undefined {
    if (this.#state.status === 'challenge' && this.#state.challenge === flow && this.#challengeUser) return this.#challengeUser;
    return undefined;
  }

  signIn(email: string, password: string) {
    this.#set({ email: normalizeEmail(email) });
    return this.#run((flows) => flows.signIn(email, password));
  }

  confirmMfa(code: string) {
    // Já entrou (uma tentativa repetida depois do sucesso): nada a fazer, e nada de erro na tela.
    if (this.#state.status === 'signedIn') return Promise.resolve(this.#state);
    const user = this.#requireChallenge('mfa');
    if (!user) return this.#fail(t`Sessão não foi iniciada`);
    return this.#run((flows) => flows.confirmMfa(user, code));
  }

  confirmMfaSetup(code: string) {
    if (this.#state.status === 'signedIn') return Promise.resolve(this.#state);
    const user = this.#requireChallenge('mfaSetup');
    if (!user) return this.#fail(t`Não foi possível confirmar o MFA. Sessão não encontrada.`);
    return this.#run((flows) => flows.confirmMfaSetup(user, code));
  }

  confirmNewPassword(newPassword: string) {
    const user = this.#requireChallenge('newPassword');
    const email = this.#state.email;
    if (!user || !email) return this.#fail(t`Sessão não foi iniciada`);
    return this.#run(async (flows) => {
      const result = await flows.completeNewPassword(user, email, newPassword);
      return result;
    });
  }

  async changePassword(oldPassword: string, newPassword: string) {
    const flows = await this.#loadFlows();
    const user = flows.currentUser();
    if (this.#state.status !== 'signedIn' || !user) return this.#fail(t`Não foi possível alterar a senha. Sessão não encontrada.`);
    return this.#run(() => flows.changePassword(user, oldPassword, newPassword));
  }

  sendPasswordRecoveryCode(email: string) {
    this.#set({ email: normalizeEmail(email), codeSent: false });
    return this.#run((flows) => flows.sendPasswordRecoveryCode(email));
  }

  /** Confirma o código e, como o legado, já entra com a senha nova. */
  confirmPasswordRecovery(email: string, code: string, password: string) {
    return this.#run(async (flows) => {
      const result = await flows.confirmPasswordRecovery(email, code, password);
      if (result.type !== 'success') return result;
      this.#set({ email: normalizeEmail(email), codeSent: false });
      return flows.signIn(email, password);
    });
  }

  async sendEmailVerificationCode() {
    const flows = await this.#loadFlows();
    const user = flows.currentUser();
    if (!user) return this.#fail(t`Não foi possível enviar o código de confirmação do e-mail. Sessão não encontrada.`);
    return this.#run(() => flows.sendEmailVerificationCode(user));
  }

  async confirmEmail(code: string) {
    const flows = await this.#loadFlows();
    const user = flows.currentUser();
    if (!user) return this.#fail(t`Não foi possível confirmar o e-mail. Sessão não encontrada.`);
    return this.#run(() => flows.confirmEmailCode(user, code));
  }

  /** Volta ao primeiro passo da recuperação ("Voltar" do legado). */
  resetCodeSent() {
    this.#set({ codeSent: undefined, error: undefined });
  }

  clearError() {
    if (this.#state.error !== undefined) this.#set({ error: undefined });
  }

  #fail(message: string) {
    this.#set({ error: message, pending: false });
    return Promise.resolve(this.#state);
  }

  /**
   * Logout local (`signOut`, não global), como o legado. Estado, fluxo, destino guardado e cache da casca
   * (`onSignedOut`) somem na hora; a promessa resolve quando os tokens saíram do armazenamento e nunca
   * rejeita: se a lib falhar, as chaves do app client são apagadas direto.
   */
  signOut(): Promise<void> {
    this.#stopPolling();
    const flows = this.#flows;
    this.#challengeUser = undefined;
    this.#inflight = undefined;
    this.#storage?.removeItem(FLOW_KEY);
    this.#storage?.removeItem(REDIRECT_KEY);
    this.#set({ status: 'signedOut' }, true);
    this.#options.onSignedOut?.();
    const local = flows ? flows.then((f) => f.currentUser()?.signOut()) : Promise.resolve();
    return local.catch(() => this.#clearCognitoStorage());
  }

  #clearCognitoStorage() {
    const storage = this.#options.cognitoStorage ?? safeLocalStorage();
    if (!storage) return;
    // eslint-disable-next-line lingui/no-unlocalized-strings -- chave da lib (CognitoUser.cleanClientData)
    const prefix = `CognitoIdentityServiceProvider.${this.#options.pool.ClientId}.`;
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
  }

  #startPolling() {
    this.#stopPolling();
    const every = this.#options.pollMs ?? 3_000;
    if (every <= 0) return;
    this.#poll = setInterval(() => {
      void this.#currentSession().then((session) => {
        if (!session && this.#state.status === 'signedIn') void this.signOut();
      });
    }, every);
  }

  #stopPolling() {
    if (this.#poll !== undefined) clearInterval(this.#poll);
    this.#poll = undefined;
  }

  /** `getSession` com uma chamada por vez: o refresh do token não dispara em paralelo. */
  #currentSession(): Promise<CognitoUserSession | null> {
    this.#inflight ??= this.#loadFlows()
      .then((flows) => {
        const user = flows.currentUser();
        return user ? flows.getSession(user) : null;
      })
      .finally(() => {
        this.#inflight = undefined;
      });
    return this.#inflight;
  }

  /** Access token para as APIs legadas; renova sozinho pelo `getSession`. Só com sessão completa. */
  async getAccessToken(): Promise<string | undefined> {
    if (this.#state.status !== 'signedIn') return undefined;
    const session = await this.#currentSession();
    // Logout no meio do refresh: a requisição em voo sai sem token.
    return this.getState().status === 'signedIn' ? session?.getAccessToken().getJwtToken() : undefined;
  }

  /** ID token (tem `aud`): é o que o BFF do Luy valida. Nunca mande para as APIs legadas. */
  async getIdToken(): Promise<string | undefined> {
    if (this.#state.status !== 'signedIn') return undefined;
    const session = await this.#currentSession();
    return this.getState().status === 'signedIn' ? session?.getIdToken().getJwtToken() : undefined;
  }

  /** Para onde voltar depois do login (o `urlFromRedirect` do legado). */
  setRedirect(href: string) {
    if (/^\/login/i.test(href)) return;
    this.#storage?.setItem(REDIRECT_KEY, href);
  }

  consumeRedirect(): string {
    const href = this.#storage?.getItem(REDIRECT_KEY) ?? '/';
    this.#storage?.removeItem(REDIRECT_KEY);
    return href;
  }

  dispose() {
    this.#stopPolling();
    this.#listeners.clear();
  }
}

let current: AuthSession | undefined;

/** Cria a sessão do app (uma vez, no boot). */
export function initAuthSession(options: AuthSessionOptions): AuthSession {
  current?.dispose();
  current = new AuthSession(options);
  return current;
}

export function getAuthSession(): AuthSession {
  if (!current) throw new Error('AuthSession ainda não criada: chame initAuthSession() no boot');
  return current;
}
